import { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UserContext } from '../Contexts/UserContext';
import { officerWorkQueueHeaders, parseOfficerWorkQueue, supervisorWorkQueueHeaders, parseSupervisorWorkQueue } from '../utils/WorkQueueHelpers';
import PaginatedTableWrapper from './PaginatedTableWrapper';
import { useImmerReducer } from "use-immer";
import { UPDATE_INCIDENT_REVIEW_ALL_FIELDS } from '../Mutations/IncidentMutations/UpdateIncidentReview';

import moment from 'moment';

const officerReducer = (state, action) => {
  switch (action.type) {
    case 'initialize':
      return { officerWorkQueue: action.payload }

    case 'update-incident-review':
      if (action.payload.property && action.payload.incidentIndex >= 0) {
        return void {
          officerWorkQueue: state.officerWorkQueue[action.payload.incidentIndex].incidentReview[action.payload.property] = action.payload.value
        }
      } else {
        return void {
          officerWorkQueue: state.officerWorkQueue.map(i => {
            if (i.id === action.payload.incidentId || i.uuid === action.payload.incidentId) {
              i.incidentReview[action.payload.property] = action.payload.value
              return i;
            } else {
              return i;
            }
          })
        }
      }

    default:
      return state;
  }
}

const supervisorReducer = (state, action) => {
  switch (action.type) {
    case 'initialize':
      return { supervisorWorkQueue: action.payload }

    case 'update-incident-review':
      if (action.payload.property && action.payload.incidentIndex >= 0) {
        return void {
          supervisorWorkQueue: state.supervisorWorkQueue[action.payload.incidentIndex].incidentReview[action.payload.property] = action.payload.value
        }
      } else {
        return void {
          supervisorWorkQueue: state.supervisorWorkQueue.map(i => {
            if (i.id === action.payload.incidentId || i.uuid === action.payload.incidentId) {
              i.incidentReview[action.payload.property] = action.payload.value
              return i;
            } else {
              return i;
            }
          })
        }
      }

    case 'remove-incident':
      return {
        supervisorWorkQueue: [...state.supervisorWorkQueue.filter(i => i.id?.toString() !== action.payload.incidentId?.toString())]
      }
    default:
      return state;
  }
}

const WorkQueue = ({
  dashSize,
  handleIncidentOpening,
  officerIncidents,
  supervisorIncidents,
  officerWorkQueueLoading,
  officerWorkQueueError,
  supervisorWorkQueueLoading,
  supervisorWorkQueueError
}) => {
  const { user, responseErrorHandler } = useContext(UserContext)
  const [{ officerWorkQueue }, officerWorkQueueDispatch] = useImmerReducer(officerReducer, { officerWorkQueue: [] })
  const [{ supervisorWorkQueue }, supervisorWorkQueueDispatch] = useImmerReducer(supervisorReducer, { supervisorWorkQueue: [] })
  // /*******This is only for development. The immer state isn't visible in the Components inspector so it's being mirrored below. Delete when done.*******/
  // const [mirror, setMirror] = useState(supervisorWorkQueue)
  // useEffect(() => { setMirror(supervisorWorkQueue) }, [supervisorWorkQueue])
  // /*******Delete when done.*******/


  useEffect(() => {
    if (officerIncidents?.length > 0) {
      officerWorkQueueDispatch({ type: 'initialize', payload: officerIncidents })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officerIncidents])

  useEffect(() => {
    if (supervisorIncidents?.length > 0) {
      supervisorWorkQueueDispatch({ type: 'initialize', payload: supervisorIncidents })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorIncidents])

  const [updateIncidentReview] = useMutation(UPDATE_INCIDENT_REVIEW_ALL_FIELDS, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: (data) => {
      if (data) {
        responseErrorHandler(data)
      }
    }
  })

  const handleKeepInOfficerQueue = (incidentIndex, incidentId, incidentReviewId, bool) => {
    officerWorkQueueDispatch({ type: 'update-incident-review', payload: { incidentIndex: incidentIndex, property: "keepInOfficerQueue", value: bool } })
    updateIncidentReview({ variables: { input: { incidentReviewId: incidentReviewId, originatorId: user?.id, incidentId: incidentId, keepInOfficerQueue: bool } } })
  }

  const handleOfficerQuickApprove = (incidentIndex, incidentId, incidentReviewId, bool) => {
    const approvalStatus = bool
      ? {
        "id": "2",
        "name": "Approved",
        "oriId": "1"
      }
      : {
        "id": "1",
        "name": "Pending",
        "oriId": "1"
      }
    officerWorkQueueDispatch({ type: 'update-incident-review', payload: { incidentIndex: incidentIndex, property: "approvalStatus", value: approvalStatus } })
    updateIncidentReview({ variables: { input: { incidentReviewId: incidentReviewId, originatorId: user?.id, incidentId: incidentId, approvalStatusId: approvalStatus.id } } })
  }

  //update to supervisor
  const handleKeepInQueue = (incidentIndex, incidentId, incidentReviewId, bool) => {
    supervisorWorkQueueDispatch({ type: 'update-incident-review', payload: { incidentIndex: incidentIndex, property: "keepInQueue", value: bool } })
    updateIncidentReview({ variables: { input: { incidentReviewId: incidentReviewId, originatorId: user?.id, incidentId: incidentId, keepInQueue: bool } } })
  }

  const handleSupervisorQuickApprove = (incidentIndex, incidentId, incidentReviewId, bool, keepInQueue) => {
    const approvalStatus = bool
      ? {
        "id": "2",
        "name": "Approved",
        "oriId": "1"
      }
      : {
        "id": "1",
        "name": "Pending",
        "oriId": "1"
      }
    const serverTimeFormat = moment().utc().format("YYYY-MM-DD HH:mm:ss UTC")
    if (keepInQueue) {
      supervisorWorkQueueDispatch({ type: 'update-incident-review', payload: { incidentIndex: incidentIndex, property: "approvalStatus", value: approvalStatus } })
    } else {
      console.log([...supervisorWorkQueue.filter(i => i.id?.toString() !== incidentId?.toString())])
      supervisorWorkQueueDispatch({ type: 'remove-incident', payload: { incidentId: incidentId } })
    }
    updateIncidentReview({ variables: { input: { incidentReviewId: incidentReviewId, originatorId: user?.id, incidentId: incidentId, approvalStatusId: approvalStatus.id, reviewDate: serverTimeFormat, reviewingSupervisorId: user?.id } } })
  }


  return (
    <div id="WorkQueue-wrapper">
      <PaginatedTableWrapper
        dashSize={dashSize}
        tableHeight={user.roleNames.includes("Sup") ? "40vh" : "80vh"}
        tableName={"Officer Work Queue"}
        tableHelpLink={"mobile_officer_work_q"}
        headers={officerWorkQueueHeaders}
        userId={user?.id}
        paginatedQueryHandler={() => console.log('Not paginated')}
        rows={officerWorkQueue ? parseOfficerWorkQueue(officerWorkQueue, handleKeepInOfficerQueue, handleOfficerQuickApprove, handleIncidentOpening, user?.roleNames) : []}
        loading={officerWorkQueueLoading}
        error={officerWorkQueueError}
        paginated={false}
        userRoles={user?.roleNames}
        resizable={false}
      />
      <br />
      {user.roleNames.includes("Sup") && <PaginatedTableWrapper
        dashSize={dashSize}
        tableHeight={"45vh"}
        tableName={"Supervisor Work Queue"}
        tableHelpLink={"mobile_sup_work_q"}
        headers={supervisorWorkQueueHeaders}
        userId={user?.id}
        paginatedQueryHandler={() => console.log('Not paginated')}
        rows={supervisorWorkQueue ? parseSupervisorWorkQueue(supervisorWorkQueue, handleKeepInQueue, handleSupervisorQuickApprove, handleIncidentOpening, user?.roleNames) : []}
        loading={supervisorWorkQueueLoading}
        error={supervisorWorkQueueError}
        paginated={false}
        userRoles={user?.roleNames}
        resizable={false}
      />}
    </div>
  );
}

export default WorkQueue;